import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

export interface ProcessStep {
  number: number;
  title: string;
  description: string;
  icon: string;
}

interface ProcessSectionProps {
  title?: string;
  subtitle?: string;
  steps?: ProcessStep[];
}

const DEFAULT_STEPS: ProcessStep[] = [
  { number: 1, icon: 'ri-file-list-3-line', title: 'Request a Quote', description: 'Contact us online or by phone. We respond fast — typically within hours, not days.' },
  { number: 2, icon: 'ri-search-eye-line', title: 'On-Site Assessment', description: 'We conduct a thorough walkthrough of your facility to understand your exact needs, challenges, and schedule.' },
  { number: 3, icon: 'ri-clipboard-line', title: 'Custom Cleaning Plan', description: 'We build a tailored cleaning plan matched specifically to your facility size, budget, and operational requirements.' },
  { number: 4, icon: 'ri-calendar-check-line', title: 'Scheduled Service', description: 'Our vetted, trained professionals execute your plan consistently and on time — every single visit.' },
  { number: 5, icon: 'ri-star-line', title: 'Ongoing Quality Assurance', description: 'We monitor quality continuously, conducting regular inspections to ensure your facility always exceeds expectations.' },
];

export default function ProcessSection({ title = 'Our Simple, Reliable Process', subtitle = 'From first contact to ongoing quality assurance — we make getting a clean facility completely effortless.', steps = DEFAULT_STEPS }: ProcessSectionProps) {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section className="py-24 bg-[#03143A] relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 opacity-5">
        <img
          src="https://public.readdy.ai/ai/img_res/cc47401c8ca0514128e0a373c2c9bcff.jpg"
          alt=""
          className="w-full h-full object-cover object-center"
        />
      </div>
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C70532]/30 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C70532]/30 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div ref={ref} className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block px-5 py-2 bg-white/10 text-white/80 text-xs font-bold uppercase tracking-widest rounded-full mb-5">How It Works</span>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-5 leading-tight">{title}</h2>
          <p className="text-lg text-white/60 max-w-2xl mx-auto leading-relaxed">{subtitle}</p>
        </div>

        <div className="relative">
          {/* Connecting line */}
          <div className="hidden lg:block absolute top-12 left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />

          <div className="grid lg:grid-cols-5 gap-8">
            {steps.map((step, i) => (
              <div
                key={step.number}
                className={`relative flex flex-col items-center text-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 120}ms` }}
              >
                {/* Step circle */}
                <div className="relative mb-6 z-10">
                  <div className="w-24 h-24 rounded-2xl bg-white/8 border border-white/15 flex flex-col items-center justify-center backdrop-blur-sm hover:bg-white/15 transition-all duration-300 group">
                    <i className={`${step.icon} text-white text-3xl mb-1 group-hover:text-[#C70532] transition-colors`} />
                  </div>
                  <div className="absolute -top-3 -right-3 w-7 h-7 bg-[#C70532] rounded-full flex items-center justify-center border-2 border-[#03143A]">
                    <span className="text-white text-xs font-black">{step.number}</span>
                  </div>
                </div>
                <h4 className="font-black text-white text-base mb-2.5 leading-snug">{step.title}</h4>
                <p className="text-sm text-white/55 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Process CTA */}
        <div className={`mt-16 text-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '650ms' }}>
          <p className="text-white/60 text-sm mb-5">Ready to get started? Most services can begin within just a few days.</p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <Link to="/contact" className="px-8 py-3.5 bg-[#C70532] hover:bg-[#a80428] text-white font-bold rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap">
              Start the Process Today
            </Link>
            <a href="tel:+17133382553" className="flex items-center gap-2 px-8 py-3.5 bg-white/10 hover:bg-white/20 text-white font-bold rounded-full border border-white/20 transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill text-[#C70532]" />
              Call (713) 338-2553
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
